import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { isEqual } from 'lodash-es';
import { fromEvent } from 'rxjs';

@Injectable()
export class FormChangeTrackerService {
  private destroyRef = inject(DestroyRef);

  private initialSnapshot = signal<Record<string, unknown> | null>(null);
  private currentSnapshot = signal<Record<string, unknown> | null>(null);

  /**
   * Reactive flag: `true` when the current form value differs from the captured
   * baseline. Value-based (not `dirty`-based), so programmatic patches/effects
   * that restore the original values do NOT count as a change.
   */
  readonly hasChanges = computed(() => {
    const initial = this.initialSnapshot();
    const current = this.currentSnapshot();
    if (!initial || !current) return false;
    return !isEqual(initial, current);
  });

  initFormTracking(form: FormGroup): void {
    this.updateInitialFormState(form);
    form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.currentSnapshot.set(this.getFormSnapshot(form)));
  }

  updateInitialFormState(form: FormGroup): void {
    const snapshot = this.getFormSnapshot(form);
    this.initialSnapshot.set(snapshot);
    this.currentSnapshot.set(snapshot);
  }

  resetFormToInitial(form: FormGroup): void {
    const initial = this.initialSnapshot();
    if (!initial) return;
    form.patchValue(initial, { emitEvent: false });
    this.currentSnapshot.set(this.getFormSnapshot(form));
  }

  preventUnload(hasChanges: () => boolean = this.hasChanges): void {
    fromEvent<BeforeUnloadEvent>(window, 'beforeunload')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (hasChanges()) {
          event.preventDefault();
        }
      });
  }

  resetFormTracking(): void {
    this.initialSnapshot.set(null);
    this.currentSnapshot.set(null);
  }

  private getFormSnapshot(form: FormGroup): Record<string, unknown> {
    const snapshot: Record<string, unknown> = {};
    Object.keys(form.controls).forEach((key) => {
      snapshot[key] = this.snapshotControl(form.get(key));
    });
    return snapshot;
  }

  private snapshotControl(control: AbstractControl | null): unknown {
    if (control instanceof FormGroup) {
      return this.getFormSnapshot(control);
    }
    if (control instanceof FormArray) {
      return control.controls.map((child) => this.snapshotControl(child));
    }
    return this.getControlValue(control);
  }

  private getControlValue(control: AbstractControl | null): unknown {
    if (!control) return null;
    const value = control.value;
    if (value instanceof Date) return value.toISOString();
    if (value instanceof File) {
      return `${value.name}:${value.size}:${value.lastModified}`;
    }
    return value;
  }
}
